/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  isProgramError,
  type Address,
  type SOLANA_ERROR__INSTRUCTION_ERROR__CUSTOM,
  type SolanaError,
} from '@solana/kit';
import { PREDICTION_MIGRATOR_PROGRAM_ADDRESS } from '../programs';

/** Unauthorized: Unauthorized */
export const PREDICTION_MIGRATOR_ERROR__UNAUTHORIZED = 0x1770; // 6000
/** QuoteMintMismatch: Quote mint mismatch */
export const PREDICTION_MIGRATOR_ERROR__QUOTE_MINT_MISMATCH = 0x1771; // 6001
/** EntryAlreadyMigrated: Entry already migrated */
export const PREDICTION_MIGRATOR_ERROR__ENTRY_ALREADY_MIGRATED = 0x1774; // 6004
/** OracleNotFinalized: Oracle not finalized */
export const PREDICTION_MIGRATOR_ERROR__ORACLE_NOT_FINALIZED = 0x1776; // 6006
/** MarketNotResolved: Market not resolved */
export const PREDICTION_MIGRATOR_ERROR__MARKET_NOT_RESOLVED = 0x1777; // 6007
/** InvalidWinnerMint: Invalid winner mint */
export const PREDICTION_MIGRATOR_ERROR__INVALID_WINNER_MINT = 0x1778; // 6008
/** InsufficientBalance: Insufficient balance */
export const PREDICTION_MIGRATOR_ERROR__INSUFFICIENT_BALANCE = 0x1779; // 6009
/** MathOverflow: Math overflow */
export const PREDICTION_MIGRATOR_ERROR__MATH_OVERFLOW = 0x177a; // 6010
/** InvalidEntryId: Invalid entry ID */
export const PREDICTION_MIGRATOR_ERROR__INVALID_ENTRY_ID = 0x177b; // 6011
/** InvalidMarket: Invalid market */
export const PREDICTION_MIGRATOR_ERROR__INVALID_MARKET = 0x177c; // 6012
/** InvalidVault: Invalid vault */
export const PREDICTION_MIGRATOR_ERROR__INVALID_VAULT = 0x177d; // 6013
/** InvalidMint: Invalid mint */
export const PREDICTION_MIGRATOR_ERROR__INVALID_MINT = 0x177e; // 6014
/** WinningEntryNotMigrated: Winning entry not migrated */
export const PREDICTION_MIGRATOR_ERROR__WINNING_ENTRY_NOT_MIGRATED = 0x177f; // 6015
/** InvalidOracle: Invalid oracle */
export const PREDICTION_MIGRATOR_ERROR__INVALID_ORACLE = 0x1781; // 6017
/** ZeroClaimableSupply: Zero claimable supply */
export const PREDICTION_MIGRATOR_ERROR__ZERO_CLAIMABLE_SUPPLY = 0x1782; // 6018
/** InvalidInput: Invalid input */
export const PREDICTION_MIGRATOR_ERROR__INVALID_INPUT = 0x1783; // 6019
/** NoPendingRewards: No pending rewards */
export const PREDICTION_MIGRATOR_ERROR__NO_PENDING_REWARDS = 0x1784; // 6020
/** ClaimableSupplyAlreadySet: Claimable supply already set */
export const PREDICTION_MIGRATOR_ERROR__CLAIMABLE_SUPPLY_ALREADY_SET = 0x1785; // 6021

export type PredictionMigratorError =
  | typeof PREDICTION_MIGRATOR_ERROR__CLAIMABLE_SUPPLY_ALREADY_SET
  | typeof PREDICTION_MIGRATOR_ERROR__ENTRY_ALREADY_MIGRATED
  | typeof PREDICTION_MIGRATOR_ERROR__INSUFFICIENT_BALANCE
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_ENTRY_ID
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_INPUT
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_MARKET
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_MINT
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_ORACLE
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_VAULT
  | typeof PREDICTION_MIGRATOR_ERROR__INVALID_WINNER_MINT
  | typeof PREDICTION_MIGRATOR_ERROR__MARKET_NOT_RESOLVED
  | typeof PREDICTION_MIGRATOR_ERROR__MATH_OVERFLOW
  | typeof PREDICTION_MIGRATOR_ERROR__NO_PENDING_REWARDS
  | typeof PREDICTION_MIGRATOR_ERROR__ORACLE_NOT_FINALIZED
  | typeof PREDICTION_MIGRATOR_ERROR__QUOTE_MINT_MISMATCH
  | typeof PREDICTION_MIGRATOR_ERROR__UNAUTHORIZED
  | typeof PREDICTION_MIGRATOR_ERROR__WINNING_ENTRY_NOT_MIGRATED
  | typeof PREDICTION_MIGRATOR_ERROR__ZERO_CLAIMABLE_SUPPLY;

let predictionMigratorErrorMessages:
  | Record<PredictionMigratorError, string>
  | undefined;
if (process.env.NODE_ENV !== 'production') {
  predictionMigratorErrorMessages = {
    [PREDICTION_MIGRATOR_ERROR__CLAIMABLE_SUPPLY_ALREADY_SET]: `Claimable supply already set`,
    [PREDICTION_MIGRATOR_ERROR__ENTRY_ALREADY_MIGRATED]: `Entry already migrated`,
    [PREDICTION_MIGRATOR_ERROR__INSUFFICIENT_BALANCE]: `Insufficient balance`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_ENTRY_ID]: `Invalid entry ID`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_INPUT]: `Invalid input`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_MARKET]: `Invalid market`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_MINT]: `Invalid mint`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_ORACLE]: `Invalid oracle`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_VAULT]: `Invalid vault`,
    [PREDICTION_MIGRATOR_ERROR__INVALID_WINNER_MINT]: `Invalid winner mint`,
    [PREDICTION_MIGRATOR_ERROR__MARKET_NOT_RESOLVED]: `Market not resolved`,
    [PREDICTION_MIGRATOR_ERROR__MATH_OVERFLOW]: `Math overflow`,
    [PREDICTION_MIGRATOR_ERROR__NO_PENDING_REWARDS]: `No pending rewards`,
    [PREDICTION_MIGRATOR_ERROR__ORACLE_NOT_FINALIZED]: `Oracle not finalized`,
    [PREDICTION_MIGRATOR_ERROR__QUOTE_MINT_MISMATCH]: `Quote mint mismatch`,
    [PREDICTION_MIGRATOR_ERROR__UNAUTHORIZED]: `Unauthorized`,
    [PREDICTION_MIGRATOR_ERROR__WINNING_ENTRY_NOT_MIGRATED]: `Winning entry not migrated`,
    [PREDICTION_MIGRATOR_ERROR__ZERO_CLAIMABLE_SUPPLY]: `Zero claimable supply`,
  };
}

export function getPredictionMigratorErrorMessage(
  code: PredictionMigratorError,
): string {
  if (process.env.NODE_ENV !== 'production') {
    return (
      predictionMigratorErrorMessages as Record<PredictionMigratorError, string>
    )[code];
  }

  return 'Error message not available in production bundles.';
}

export function isPredictionMigratorError<
  TProgramErrorCode extends PredictionMigratorError,
>(
  error: unknown,
  transactionMessage: {
    instructions: Record<number, { programAddress: Address }>;
  },
  code?: TProgramErrorCode,
): error is SolanaError<typeof SOLANA_ERROR__INSTRUCTION_ERROR__CUSTOM> &
  Readonly<{ context: Readonly<{ code: TProgramErrorCode }> }> {
  return isProgramError<TProgramErrorCode>(
    error,
    transactionMessage,
    PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
    code,
  );
}
